import React from "react";
import { 
  FiX, 
  FiTag, 
  FiCalendar, 
  FiClock, 
  FiDollarSign, 
  FiUsers,
  FiImage,
  FiVideo,
  FiShield,
  FiBookOpen,
  FiLayers,
  FiUser,
  FiAward,
  FiStar,
  FiCheckCircle,
  FiXCircle,
  FiAlertCircle,
  FiClock as FiClockIcon,
  FiCalendar as FiCalendarIcon,
  FiMapPin,
  FiType,
  FiFileText,
  FiTrendingUp,
  FiEye,
  FiDownload
} from "react-icons/fi";
import { 
  MdOutlineClose, 
  MdOutlineImage,
  MdOutlineVideoLibrary,
  MdOutlineCategory,
  MdOutlineCalendarToday,
  MdOutlineAccessTime,
  MdOutlinePerson,
  MdOutlineAttachMoney,
  MdOutlineLocalOffer,
  MdOutlineVerified,
  MdOutlineCancel,
  MdOutlinePending
} from "react-icons/md";
import { 
  IoCloseOutline, 
  IoImageOutline, 
  IoVideocamOutline,
  IoCalendarOutline,
  IoTimeOutline,
  IoPersonOutline,
  IoCashOutline,
  IoPricetagOutline
} from "react-icons/io5";
import {
  FaBook,
  FaLayerGroup,
  FaChalkboardTeacher,
  FaShieldAlt,
  FaCalendarAlt,
  FaClock,
  FaTag,
  FaCheckCircle,
  FaTimesCircle,
  FaDollarSign,
  FaStar,
  FaUser,
  FaImage,
  FaVideo
} from "react-icons/fa";

const fmt = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "—";
  }
};

const fmtTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
};

const Badge = ({ children, color = "blue", icon: Icon }) => {
  const map = {
    blue: "bg-blue-50 text-blue-700 border-blue-200",
    green: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    violet: "bg-violet-50 text-violet-700 border-violet-200",
    gray: "bg-gray-50 text-gray-600 border-gray-200",
    rose: "bg-rose-50 text-rose-700 border-rose-200",
    purple: "bg-purple-50 text-purple-700 border-purple-200",
  };
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border ${map[color] || map.gray}`}
    >
      {Icon && <Icon className="w-3 h-3" />}
      {children}
    </span>
  );
};

const InfoRow = ({ icon: Icon, label, value, color = "text-slate-400" }) => (
  <div className="flex items-start gap-3 group">
    <span className={`mt-0.5 ${color} shrink-0`}>
      {Icon && <Icon className="w-4 h-4" />}
    </span>
    <div className="min-w-0 flex-1">
      <p className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold">
        {label}
      </p>
      <p className="text-sm text-slate-700 font-medium break-words">
        {value ?? "—"}
      </p>
    </div>
  </div>
);

const Section = ({ title, icon: Icon, children }) => (
  <div className="rounded-xl bg-slate-50/80 border border-slate-200 p-4 space-y-3 hover:bg-slate-50 transition-colors duration-200">
    <div className="flex items-center gap-2">
      {Icon && <Icon className="w-4 h-4 text-[#715EFE]" />}
      <h4 className="text-xs font-bold uppercase tracking-widest text-slate-400">
        {title}
      </h4>
    </div>
    {children}
  </div>
);

const CoursePanel = ({ data }) => {
  const c = data?.courses;
  if (!c)
    return (
      <p className="text-sm text-slate-400 italic">
        No course details available.
      </p>
    );
  return (
    <Section title="Course Details" icon={FaBook}>
      <div className="flex items-start gap-3">
        {c.thumbUrl && (
          <img
            src={c.thumbUrl}
            alt="thumb"
            className="w-14 h-14 rounded-lg object-cover border border-slate-200 shrink-0"
          />
        )}
        <div className="space-y-1">
          <p className="font-semibold text-slate-800">{c.title ?? "—"}</p>
          <p className="text-xs text-slate-500 leading-relaxed line-clamp-2">
            {c.desc ?? "—"}
          </p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 pt-1">
        <InfoRow
          icon={FaClock}
          label="Duration"
          value={c.duration ? `${c.duration?.toFixed(2)} min` : "—"}
        />
        <InfoRow
          icon={FaDollarSign}
          label="Price"
          value={c.isPaid ? `$${c.price ?? 0}` : "Free"}
        />
        <InfoRow
          icon={FaStar}
          label="Rating"
          value={`${c.rating ?? 0} (${c.totalUserRating ?? 0} reviews)`}
        />
      </div>
    </Section>
  );
};

const BatchPanel = ({ data }) => {
  const b = data?.batches;
  if (!b)
    return (
      <p className="text-sm text-slate-400 italic">
        No batch details available.
      </p>
    );
  const slots = b?.availability?.[0]?.slots ?? [];
  return (
    <Section title="Batch Details" icon={FaLayerGroup}>
      <div className="flex items-start gap-3">
        {b.thumbUrl && (
          <img
            src={b.thumbUrl}
            alt="thumb"
            className="w-14 h-14 rounded-lg object-cover border border-slate-200 shrink-0"
          />
        )}
        <div className="space-y-1">
          <p className="font-semibold text-slate-800">{b.title ?? "—"}</p>
          <p className="text-xs text-slate-500 leading-relaxed line-clamp-2">
            {b.desc ?? "—"}
          </p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 pt-1">
        <InfoRow
          icon={FaDollarSign}
          label="Price"
          value={b.isPaid ? `$${b.price ?? 0}` : "Free"}
        />
        <InfoRow
          icon={FaCalendarAlt}
          label="First Date"
          value={fmt(b?.availability?.[0]?.date)}
        />
      </div>
      {slots.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400">
            Slots
          </p>
          {slots.map((slot, i) => (
            <div
              key={slot?._id ?? i}
              className="flex items-center justify-between bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs text-slate-600 hover:border-[#715EFE]/30 transition-colors"
            >
              <span>{fmtTime(slot?.startTime)}</span>
              <span className="text-slate-300">→</span>
              <span>{fmtTime(slot?.endTime)}</span>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
};

const BookPanel = ({ data }) => {
  const b = data?.books;
  if (!b)
    return (
      <p className="text-sm text-slate-400 italic">
        No book details available.
      </p>
    );
  return (
    <Section title="Book Details" icon={FaBook}>
      <div className="flex items-start gap-3">
        {b.thumbUrl && (
          <img
            src={b.thumbUrl}
            alt="thumb"
            className="w-14 h-14 rounded-lg object-cover border border-slate-200 shrink-0"
          />
        )}
        <div className="space-y-1">
          <p className="font-semibold text-slate-800">{b.title ?? "—"}</p>
          <p className="text-xs text-slate-500 leading-relaxed line-clamp-2">
            {b.desc ?? "—"}
          </p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 pt-1">
        <InfoRow
          icon={FaDollarSign}
          label="Price / hr"
          value={b.pricePerHour != null ? `$${b.pricePerHour}` : "—"}
        />
      </div>
    </Section>
  );
};

const typeConfig = {
  Admin: { icon: FaShieldAlt, color: "blue", badge: "blue" },
  Course: { icon: FaChalkboardTeacher, color: "violet", badge: "violet" },
  Book: { icon: FaBook, color: "amber", badge: "amber" },
  Batch: { icon: FaLayerGroup, color: "green", badge: "green" },
};

const accentMap = {
  blue: "from-blue-500 to-blue-600",
  violet: "from-violet-500 to-violet-600",
  amber: "from-amber-500 to-amber-600",
  green: "from-emerald-500 to-emerald-600",
};

const ViewBanner = ({ onClose, data }) => {
  if (!data) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex justify-center items-center p-4 z-50 animate-in fade-in duration-200">
        <div className="bg-white rounded-2xl shadow-2xl p-10 text-center max-w-sm w-full animate-in zoom-in-95 duration-200">
          <div className="p-4 bg-rose-50 rounded-full w-fit mx-auto mb-4">
            <FiXCircle className="text-rose-400 text-4xl" />
          </div>
          <p className="text-slate-600 font-medium">No banner data available.</p>
          <button
            onClick={onClose}
            className="mt-5 px-6 py-2.5 bg-slate-800 text-white rounded-lg text-sm hover:bg-slate-700 transition-all duration-200"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  const cfg = typeConfig[data?.type] || {
    icon: FaShieldAlt,
    color: "blue",
    badge: "gray",
  };
  const accent = accentMap[cfg.color] || accentMap.blue;
  const isMediaVideo = data?.mediaType === "video";
  const TypeIcon = cfg.icon;

  const statusConfig = {
    approved: { color: "green", icon: FiCheckCircle, label: "Approved" },
    pending: { color: "amber", icon: FiClockIcon, label: "Pending" },
    rejected: { color: "rose", icon: FiXCircle, label: "Rejected" },
    expired: { color: "gray", icon: FiAlertCircle, label: "Expired" },
  };
  const status = statusConfig[data?.status] || statusConfig.pending;
  const StatusIcon = status.icon;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center p-4 z-50 animate-in fade-in duration-200">
      <div className="bg-white w-full max-w-3xl max-h-[90vh] rounded-2xl shadow-2xl relative flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">
        {/* Header */}
        <div
          className={`bg-gradient-to-r ${accent} px-6 pt-5 pb-5 relative flex-shrink-0`}
        >
          <button
            onClick={onClose}
            className="absolute top-3 right-3 text-white/70 hover:text-white transition-colors duration-200 p-1 rounded-lg hover:bg-white/10"
            aria-label="Close"
          >
            <FiX className="w-6 h-6" />
          </button>
          
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <div className="flex items-center gap-2">
              <span className="text-white/80">
                <TypeIcon className="w-4 h-4" />
              </span>
              <Badge color={cfg.badge} icon={TypeIcon}>
                {data?.type ?? "Unknown"}
              </Badge>
            </div>
            <Badge color={status.color} icon={StatusIcon}>
              <span className="capitalize">{data?.status?.split("_").join(" ") || "—"}</span>
            </Badge>
            {data?.isDisable && (
              <Badge color="gray" icon={FiXCircle}>Disabled</Badge>
            )}
          </div>
          
          <h2 className="text-white font-bold text-xl leading-tight line-clamp-2">
            {data?.title ?? "Untitled Banner"}
          </h2>
        </div>

        {/* Body */}
        <div className="px-6 pt-5 pb-6 space-y-4 flex-1 overflow-y-auto">
          {/* Media */}
          <div className="rounded-xl overflow-hidden shadow-md border border-slate-200 bg-slate-100">
            {isMediaVideo ? (
              <video
                src={data?.videoUrl || data?.imageUrl}
                poster={data?.imageUrl}
                controls
                className="w-full h-56 object-cover bg-black"
              />
            ) : (
              <img
                src={data?.imageUrl}
                alt={data?.title ?? "Banner"}
                className="w-full h-56 object-cover"
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            )}
          </div>

          {/* User Info */}
          {data?.type !== "Admin" && data?.user && (
            <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 hover:bg-slate-100 transition-colors duration-200">
              <img
                src={data.user?.profileImage}
                alt={data.user?.fullName ?? "User"}
                className="w-10 h-10 rounded-full object-cover border-2 border-slate-200"
                onError={(e) => {
                  e.target.style.background = "#e2e8f0";
                  e.target.src = "";
                }}
              />
              <div>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold">
                  Requested by
                </p>
                <p className="text-sm font-semibold text-slate-700">
                  {data.user?.fullName ?? "—"}
                </p>
              </div>
              {data.user?.createdAt && (
                <p className="ml-auto text-xs text-slate-400">
                  Joined {fmt(new Date(data.user.createdAt).toISOString())}
                </p>
              )}
            </div>
          )}

          {/* Plan Info */}
          {data?.planId && (
            <Section title="Subscription Plan" icon={FiDollarSign}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-slate-800">
                    {data.planId?.title ?? "—"}
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {data.planId?.durationInDays ?? "—"} days
                  </p>
                </div>
                <span className="text-lg font-bold text-emerald-600">
                  ${data.planId?.price ?? "—"}
                </span>
              </div>
            </Section>
          )}

          {/* Timeline */}
          <Section title="Timeline" icon={FiCalendar}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <InfoRow
                icon={FiCalendar}
                label="Approved At"
                value={fmt(data?.approveAt)}
                color="text-emerald-500"
              />
              <InfoRow
                icon={FiCalendar}
                label="Expires At"
                value={fmt(data?.expireAt)}
                color="text-rose-500"
              />
              {data?.startDate && (
                <InfoRow
                  icon={FiCalendar}
                  label="Start Date"
                  value={fmt(data?.startDate)}
                />
              )}
              {data?.endDate && (
                <InfoRow
                  icon={FiCalendar}
                  label="End Date"
                  value={fmt(data?.endDate)}
                />
              )}
            </div>
          </Section>

          {/* Meta */}
          <Section title="Meta" icon={FiTag}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <InfoRow
                icon={FiMapPin}
                label="Place At"
                value={data?.placeAt ?? "—"}
              />
              <InfoRow
                icon={FiType}
                label="Media Type"
                value={data?.mediaType ?? "—"}
              />
            </div>
          </Section>

          {/* Dynamic Panels */}
          {data?.type === "Course" && <CoursePanel data={data} />}
          {data?.type === "Batch" && <BatchPanel data={data} />}
          {data?.type === "Book" && <BookPanel data={data} />}

          {data?.type === "Admin" && (
            <div className="rounded-xl bg-blue-50 border border-blue-200 p-4 text-sm text-blue-700 font-medium flex items-center gap-2">
              <FaShieldAlt className="text-blue-500" /> This is an Admin-managed banner.
            </div>
          )}

          {/* Footer */}
          <div className="flex justify-end pt-2">
            <button
              onClick={onClose}
              className={`bg-gradient-to-r ${accent} text-white text-sm font-semibold px-6 py-2.5 rounded-xl shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all duration-200`}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewBanner;